/**
 * Fictional sample data for the demo store, in two tenants (ADR 0004). The
 * demo is the product's marketing showcase, so nothing in here may point at
 * a real club, person or aircraft (`demoSeed.guard.test.ts` holds that):
 *
 *  - Flugsportverein Musterstadt e.V. (`musterstadt`, code FSVM): five
 *    aircraft, a dozen members and 28 damages across every status and
 *    category, a few of them deliberately close together so the nearby
 *    warning and the clustering show.
 *  - Luftsportgruppe Beispieltal (`beispieltal`, code LSGB): a DA40 and a
 *    C172, four members, a handful of damages. Jonas Becker is editor in
 *    Musterstadt and admin here.
 *
 * Fleet admin: Musterstadt has its own type MS-1 (fictional, drawn with the
 * free demo set's PA-28 drawings) on the out-of-service D-EXAF, and D-EXAZ in
 * the trash; Beispieltal has no own type, so the demo shows both cases.
 *
 * Registrations use the range D-EX.. on purpose: it marks demo aircraft at a
 * glance (D-EXA. Musterstadt, D-EXB. Beispieltal). Addresses are example.org.
 *
 * Nobody is platform manager: the platform area is not part of the demo
 * (demoPersonas.ts says why).
 *
 * Dates are relative to the moment of seeding, so "overdue" stays overdue.
 *
 * Coordinates of mirrored views (unten/rechts/hinten) are given the way
 * core's boardPointToNormalized stores them: x refers to the UNMIRRORED base
 * image (a tap at displayed x is stored as 1 − x). The door on the right side
 * at displayed x≈0.6 is therefore stored as 0.4.
 */
import { DEFAULT_DUPLICATE_RADIUS, DEFAULT_MEMBER_PARTICIPATION, DEFAULT_REVIEW_DUE_DAYS, formatDamageNumber, normalizeCategories, type AircraftView, type DamageCategory, type DamageStatus } from '@fsv/core';
import type { Settings, TenantRole } from './types';
import type {
  DemoState,
  StoredAircraft,
  StoredDrawingUpload,
  StoredType,
  StoredComment,
  StoredDamage,
  StoredEvent,
  StoredImage,
  StoredMembership,
  StoredNotice,
  StoredNoticeAck,
  StoredPerson,
  StoredTenant,
} from './demoState';
import { DEMO_STATE_VERSION } from './demoState';
import { DEMO_PHOTOS, demoPhotoRef, type DemoPhotoName } from './demoPhotos';

export const DEMO_USERS = {
  member: 'm-demir',
  editor: 'm-becker',
  admin: 'm-albrecht',
} as const;

export const DEMO_TENANTS = {
  musterstadt: 'tn-musterstadt',
  beispieltal: 'tn-beispieltal',
} as const;

/** Slug of the club every /demo link lands in. */
export const DEMO_HOME_SLUG = 'musterstadt';

export const DEFAULT_SETTINGS: Settings = { memberParticipation: DEFAULT_MEMBER_PARTICIPATION, duplicateRadius: DEFAULT_DUPLICATE_RADIUS, reviewDueDays: DEFAULT_REVIEW_DUE_DAYS, mediaRequired: false, aircraftGrounding: false };

const DAY = 86_400_000;

function person(
  id: string,
  displayName: string,
  daysAgoSignIn: number | null,
  now: number,
  extra: Partial<StoredPerson> = {},
): StoredPerson {
  const [first = '', last = ''] = displayName
    .toLowerCase()
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ä/g, 'ae')
    .split(' ');
  return {
    id,
    email: `${first}.${last}@example.org`,
    displayName,
    lastSignInAt:
      daysAgoSignIn === null
        ? null
        : new Date(now - daysAgoSignIn * DAY - 3_600_000 * 3).toISOString(),
    createdAt: new Date(now - 400 * DAY).toISOString(),
    providers: ['email-otp'],
    ...extra,
  };
}

type Row = [
  reg: string,
  view: AircraftView,
  x: number,
  y: number,
  /** One type, or several (0021) — written in any order, stored in catalogue order. */
  category: DamageCategory | readonly DamageCategory[],
  status: DamageStatus,
  title: string,
  description: string | null,
  daysAgo: number,
  reporter: string,
  photos: number,
  note?: string,
  mergedInto?: number,
];

const ROWS: Row[] = [
  [
    'D-EXAA',
    'oben',
    0.195,
    0.303,
    ['kratzer', 'lack'],
    'bekannt',
    'Kratzer Flügelvorderkante links',
    'Langer Kratzer an der Vorderkante, ca. 30 cm, Lack durch. Kein Materialabtrag, laut Werft unkritisch.',
    210,
    'm-becker',
    2,
  ],
  [
    'D-EXAA',
    'oben',
    0.22,
    0.309,
    'lack',
    'beobachtung',
    'Lackabplatzer neben dem Kratzer',
    'Abplatzung ca. 1 cm. Wird bei der 50-h-Kontrolle angesehen.',
    120,
    'm-demir',
    1,
    'Bei 50-h-Kontrolle prüfen, bis dahin beobachten.',
  ],
  [
    'D-EXAA',
    'oben',
    0.18,
    0.322,
    'lack',
    'gemeldet',
    'Macke Flügelvorderkante links – neu?',
    'Beim Vorflugcheck gesehen: kleine Macke im Lack an der Vorderkante, bis auf den Untergrund. Vielleicht gehört sie zum bekannten Kratzer, kam mir aber neu vor.',
    2,
    'm-nguyen',
    1,
  ],
  [
    'D-EXAA',
    // Was „Steinschläge Cowling links“ on the left view until its photos (29.10.2026).
    'oben',
    0.08,
    0.28,
    'lack',
    'bekannt',
    'Steinschläge Flügelvorderkante links',
    'Viele kleine Steinschläge an der Unterseite der Vorderkante, außen. Laminat heil, wird bei der nächsten Lackausbesserung mitgemacht.',
    300,
    'm-hartmann',
    1,
  ],
  [
    'D-EXAA',
    'vorne',
    0.619,
    0.859,
    ['lack', 'riss'],
    'in_reparatur',
    'Lack an der Radverkleidung links brüchig',
    'Vorn an der Spitze feine Risse im Lack, an mehreren Stellen abgeplatzt. Vermutlich vom Rollen über die Kante am Waschplatz. Neue Verkleidung ist bestellt.',
    40,
    'm-kowalski',
    2,
  ],
  [
    'D-EXAA',
    // Was on „hinten“ until 09.10.2026; the rudder tip seen from the left side.
    'links',
    0.93,
    0.12,
    'riss',
    'behoben',
    'Haarriss Randbogen Seitenruder',
    'Riss ca. 2 cm, gestoppt und laminiert.',
    400,
    'm-becker',
    1,
  ],
  [
    'D-EXAA',
    'unten',
    0.5,
    0.458,
    'kratzer',
    'gemeldet',
    'Kratzer Rumpfunterseite',
    'Längs, etwa handbreit hinter dem Fahrwerk.',
    11,
    'm-schaefer',
    0,
  ],
  [
    'D-EXAA',
    'rechts',
    0.42,
    0.37,
    'sonstiges',
    'bekannt',
    'Türdichtung rechts eingerissen',
    'Dichtung oben hinten auf ca. 3 cm eingerissen, Tür schließt normal.',
    90,
    'm-demir',
    1,
    'Dichtung wird beim nächsten Werftbesuch getauscht.',
  ],
  [
    'D-EXAB',
    'oben',
    0.727,
    0.322,
    'sonstiges',
    'bekannt',
    'Dichtung am Tankdeckel rechts löst sich',
    'Die Dichtung um den Tankstutzen steht auf etwa einem Drittel des Umfangs hoch. Deckel schließt dicht, kein Kraftstoffgeruch.',
    150,
    'm-hartmann',
    1,
  ],
  [
    'D-EXAB',
    'oben',
    0.5,
    0.11,
    'lack',
    'in_reparatur',
    'Lackschaden an der Cowling hinter dem Spinner',
    'Ca. 2 cm, Lack bis auf den Untergrund ab, vermutlich Steinschlag. Cowling ist zum Ausbessern beim Lackierer angemeldet.',
    30,
    'm-kowalski',
    1,
  ],
  [
    'D-EXAB',
    'links',
    0.893,
    0.273,
    'beule',
    'beobachtung',
    'Kleine Delle Seitenleitwerk',
    'Durchmesser ca. 15 mm, ohne Knick. Alle 25 h kontrollieren.',
    75,
    'm-becker',
    2,
  ],
  [
    'D-EXAB',
    'oben',
    0.019,
    0.322,
    'kratzer',
    'verworfen',
    'Kratzer Randbogen links',
    'Schwarzer Strich am Randbogen.',
    20,
    'm-osei',
    1,
    'Nur Gummiabrieb vom Hangartor, ließ sich abwischen.',
  ],
  [
    'D-EXAB',
    'oben',
    0.273,
    0.322,
    'kratzer',
    'gemeldet',
    'Kratzer neben dem Tankdeckel links?',
    'Ein paar kurze Kratzer im Lack schräg unterhalb des Tankdeckels, vermutlich vom Zapfhahn. Bitte ansehen.',
    9,
    'm-brandt',
    2,
  ],
  [
    'D-EXAC',
    'vorne',
    0.5,
    0.524,
    'kratzer',
    'bekannt',
    'Kerbe Propellerblatt oben',
    'Leichte Kerbe an der Vorderkante, laut Werft innerhalb der Toleranz.',
    60,
    'm-hartmann',
    1,
  ],
  [
    'D-EXAC',
    'links',
    0.397,
    0.262,
    'lack',
    'bekannt',
    'Lack an der Haubenkante',
    'Unter der Haube an der Kante der Verkleidung an zwei Stellen abgeplatzt, an der Schraube ca. 3 cm. Vom Haubenrahmen beim Öffnen.',
    180,
    'm-becker',
    1,
  ],
  [
    'D-EXAC',
    'oben',
    0.417,
    0.364,
    'kratzer',
    'behoben',
    'Kratzer Flügel links innen',
    'Kratzer vor der Trittfläche an der Flügelwurzel, beim Betanken entstanden.',
    250,
    'm-kowalski',
    1,
  ],
  [
    'D-EXAC',
    'oben',
    0.603,
    0.301,
    'kratzer',
    'gemeldet',
    'Kratzer an der Schraube Flügelwurzel rechts',
    'Kurzer Kratzer im Lack direkt neben der Schraube, vermutlich vom Schraubendreher. Bitte ansehen.',
    0,
    'm-haddad',
    1,
  ],
  [
    'D-EXAC',
    'links',
    0.407,
    0.273,
    'lack',
    'verworfen',
    'Haube Rahmen verkratzt',
    null,
    50,
    'm-petersen',
    1,
    'Duplikat von FSVM-000015',
    15,
  ],
  [
    'D-EXAD',
    'oben',
    0.5,
    0.102,
    'beule',
    'bekannt',
    'Hageldellen auf der Cowling',
    'Nach dem Hagel im Mai: viele flache Dellen auf der oberen Cowling, Lack heil. Werft klärt, ob die Cowling getauscht werden muss.',
    100,
    'm-kowalski',
    2,
  ],
  [
    'D-EXAD',
    'links',
    0.257,
    0.609,
    'kratzer',
    'beobachtung',
    'Kratzer an der Strebe links',
    'Unten an der Strebe, vermutlich vom Schlepper.',
    45,
    'm-hartmann',
    1,
  ],
  [
    'D-EXAD',
    'vorne',
    0.5,
    0.887,
    'sonstiges',
    'bekannt',
    'Korrosion Bugradgabel',
    'Oberflächlich, bei der Jahresnachprüfung behandeln.',
    70,
    'm-becker',
    1,
  ],
  [
    'D-EXAD',
    'unten',
    0.5,
    0.605,
    'lack',
    'gemeldet',
    'Lackabplatzer Rumpfunterkante am Fahrwerk',
    'Kleiner Abplatzer an der unteren Kante, direkt über der Radverkleidung, Grundierung sichtbar.',
    4,
    'm-lorenz',
    1,
  ],
  [
    'D-EXAD',
    'rechts',
    0.335,
    0.554,
    'kratzer',
    'behoben',
    'Kratzer Tür rechts',
    null,
    500,
    'm-hartmann',
    0,
  ],
  [
    'D-EXAE',
    'oben',
    0.5,
    0.122,
    'lack',
    'bekannt',
    'Lackabplatzer Cowling oben',
    'An der Fuge zwischen den Cowling-Hälften, ca. 3 cm, bis auf den Untergrund.',
    160,
    'm-hartmann',
    2,
  ],
  [
    'D-EXAE',
    'links',
    0.397,
    0.611,
    'beule',
    'beobachtung',
    'Delle Flügelwurzel links',
    'Trittstelle neben der Antirutschfläche.',
    25,
    'm-becker',
    1,
  ],
  [
    'D-EXAE',
    'vorne',
    0.366,
    0.906,
    'kratzer',
    'behoben',
    'Radverkleidung rechts verkratzt',
    null,
    330,
    'm-kowalski',
    1,
  ],
  [
    'D-EXAE',
    'oben',
    0.945,
    0.373,
    'kratzer',
    'gemeldet',
    'Kratzer Flügel rechts außen – neu?',
    'Mehrere Kratzer im Lack auf der Flügeloberseite, nahe am Randbogen. Weiß nicht, ob die schon bekannt sind.',
    6,
    'm-nguyen',
    1,
  ],
  [
    'D-EXAE',
    'oben',
    0.96,
    0.383,
    'kratzer',
    'bekannt',
    'Zwei Kratzer Flügeloberseite rechts außen',
    'Zwei parallele Kratzer, ca. 15 cm, vom Hallentor. Lack durch, Blech unbeschädigt.',
    140,
    'm-kowalski',
    1,
  ],
  // The cabin (innen): coordinates on the built-in cabin schematic (1000 × 1500, nose up).
  [
    'D-EXAB',
    'innen',
    0.375,
    0.44,
    'sonstiges',
    'bekannt',
    'Sitzbezug vorn links eingerissen',
    'Naht an der Seitenwange auf ca. 5 cm offen, Polster sichtbar.',
    21,
    'm-schaefer',
    0,
    'Bezug ist bestellt, bis dahin abgeklebt.',
  ],
  [
    'D-EXAC',
    'innen',
    0.6,
    0.25,
    'kratzer',
    'in_reparatur',
    'Kratzer am Instrumentenbrett',
    'Rechts neben dem Funkgerät, nur die Oberfläche.',
    35,
    'm-becker',
    0,
  ],
  // „Akzeptiert – bleibt so“ (0016): seen, judged, deliberately left. Last in
  // the list so the numbers of every damage above stay what they were.
  [
    'D-EXAA',
    'oben',
    0.614,
    0.343,
    'lack',
    'akzeptiert',
    'Abgenutzte Trittmarke Flügelwurzel rechts',
    'Die Trittmarke ist zur Hälfte abgerieben, an der Vorderkante darunter zwei kleine Abplatzer.',
    75,
    'm-nguyen',
    1,
    'Rein optisch, Laminat geprüft – bleibt so bis zur nächsten Lackierung. Bitte nicht erneut melden.',
  ],
];

const MST_EDITORS = new Set(['m-becker', 'm-hartmann', 'm-kowalski', 'm-albrecht']);

/**
 * Real photos (demoPhotos.ts, provenance in apps/web/public/demo-photos/SOURCES.md)
 * by damage title; they replace the row's generated placeholders. Every other
 * damage keeps its placeholders. Title and text of each row here describe its
 * photo. Keyed by title, not number: a renamed row loses its photo, and
 * demoPhotos.test.ts fails when a photo is shown nowhere.
 */
const MST_PHOTOS: Readonly<Record<string, readonly DemoPhotoName[]>> = {
  'Kratzer Flügelvorderkante links': ['vorderkante-kratzer'], // FSVM-000001, D-EXAA oben – the site's „detail“ shot
  'Lackabplatzer neben dem Kratzer': ['fluegel-lackabplatzer'], // 000002, D-EXAA oben
  'Lack an der Radverkleidung links brüchig': ['radverkleidung-lack'], // 000005, D-EXAA vorne
  'Dichtung am Tankdeckel rechts löst sich': ['tankdeckel-dichtung'], // 000009, D-EXAB oben
  'Lackschaden an der Cowling hinter dem Spinner': ['cowling-lackabplatzer'], // 000010, D-EXAB oben
  'Hageldellen auf der Cowling': ['hagel-cowling'], // 000019, D-EXAD oben
  'Lackabplatzer Cowling oben': ['cowling-fuge-abplatzer'], // 000024, D-EXAE oben
  'Zwei Kratzer Flügeloberseite rechts außen': ['kratzer-oberseite'], // 000028, D-EXAE oben
  // Second batch (29.10.2026).
  'Macke Flügelvorderkante links – neu?': ['vorderkante-macke'], // 000003, D-EXAA oben, near 000001
  'Steinschläge Flügelvorderkante links': ['vorderkante-steinschlaege', 'vorderkante-steinschlaege-nah'], // 000004, D-EXAA oben
  'Kratzer neben dem Tankdeckel links?': ['tankdeckel-uebersicht', 'tankdeckel-kratzer'], // 000013, D-EXAB oben
  'Lack an der Haubenkante': ['haube-verkleidung-abplatzer'], // 000015, D-EXAC links, 000018 merged into it
  'Kratzer Flügel links innen': ['trittflaeche-kratzer'], // 000016, D-EXAC oben
  'Kratzer an der Schraube Flügelwurzel rechts': ['schraube-kratzer'], // 000017, D-EXAC oben
  'Lackabplatzer Rumpfunterkante am Fahrwerk': ['rumpfunterkante-abplatzer'], // 000022, D-EXAD unten
  'Kratzer Flügel rechts außen – neu?': ['kratzer-fluegel-hand', 'kratzer-fluegel-nah'], // 000027, D-EXAE oben – the same scratches as 000028
  'Abgenutzte Trittmarke Flügelwurzel rechts': ['trittmarke-abgenutzt'], // 000031, D-EXAA oben, accepted
};

/** Luftsportgruppe Beispieltal: same drawings (DA40, C172), its own numbering from LSGB-000001. */
const BSP_ROWS: Row[] = [
  ['D-EXBA', 'oben', 0.8, 0.332, 'kratzer', 'bekannt', 'Kratzer Flügelhinterkante rechts', 'Ca. 4 cm, vom Abstellen am Zaun.', 80, 'm-morina', 1],
  ['D-EXBA', 'vorne', 0.5, 0.5, 'riss', 'gemeldet', 'Riss am Spinner?', 'Feine Linie an der Spinnerkappe, bitte ansehen.', 3, 'm-arslan', 1],
  ['D-EXBA', 'links', 0.25, 0.47, 'beule', 'beobachtung', 'Delle Rumpf unter der Haube', 'Flach, ca. 2 cm. Bei jeder 25-h-Kontrolle prüfen.', 40, 'm-becker', 2],
  ['D-EXBB', 'oben', 0.138, 0.312, 'lack', 'in_reparatur', 'Lackschaden Flügel links außen', 'Abgeplatzt nach Hagel, Lackierer ist beauftragt.', 25, 'm-morina', 1],
  ['D-EXBB', 'vorne', 0.5, 0.887, 'sonstiges', 'behoben', 'Bugradreifen abgefahren', null, 150, 'm-wagner', 1],
  ['D-EXBB', 'links', 0.324, 0.609, 'kratzer', 'gemeldet', 'Kratzer an der Tür links', 'Unterhalb des Griffs, ca. 8 cm.', 9, 'm-wagner', 0],
];
const BSP_EDITORS = new Set(['m-becker', 'm-morina']);

interface SeedContext {
  now: number;
  events: Omit<StoredEvent, 'id'>[];
  damages: StoredDamage[];
  images: StoredImage[];
}

const pad = (n: number) => String(n).padStart(4, '0');
const isoOf = (ms: number) => new Date(ms).toISOString();

/** File name stamp of an export, like the API's: 2026-10-03T10-00-00Z. */
export function exportStamp(iso: string): string {
  return iso.slice(0, 19).replace(/:/g, '-') + 'Z';
}

/** Damages, photos and their events for one tenant. Returns the stored damages of that tenant. */
function seedDamages(
  ctx: SeedContext,
  tenantId: string,
  code: string,
  rows: Row[],
  aircraft: StoredAircraft[],
  editors: Set<string>,
  reviewer: string,
  idPrefix: string,
  realPhotos: Readonly<Record<string, readonly DemoPhotoName[]>> = {},
): StoredDamage[] {
  const { now } = ctx;
  const byReg = new Map(aircraft.map((a) => [a.registration, a]));
  const created: StoredDamage[] = [];
  const pushEvent = (e: Omit<StoredEvent, 'id' | 'tenantId'>) => ctx.events.push({ ...e, tenantId });

  rows.forEach((row, index) => {
    const [reg, view, x, y, type, status, title, description, daysAgo, reporter, placeholders, note, mergedInto] = row;
    const categories = normalizeCategories(Array.isArray(type) ? type : [type]);
    if (!categories) throw new Error(`seed: unknown category in row ${index + 1}`);
    const category = categories[0]!;
    const number = index + 1;
    const id = `d-${idPrefix}${pad(number)}`;
    const reportedMs = now - daysAgo * DAY - (index % 5) * 3_600_000 - 2 * 3_600_000;
    const byMember = !editors.has(reporter);
    const reviewedMs = reportedMs + 2 * DAY;
    const reviewed = byMember && status !== 'gemeldet';
    const resolvedMs = status === 'behoben' ? reportedMs + 20 * DAY : null;
    const a = byReg.get(reg);
    if (!a) throw new Error(`seed: unknown aircraft ${reg}`);
    const d: StoredDamage = {
      id,
      tenantId,
      number,
      aircraftId: a.id,
      categories,
      view,
      x,
      y,
      title,
      description,
      status,
      reportedBy: reporter,
      reportedAt: isoOf(reportedMs),
      updatedAt: isoOf(resolvedMs ?? (reviewed ? reviewedMs : reportedMs)),
      resolvedAt: resolvedMs ? isoOf(resolvedMs) : null,
      reviewedBy: reviewed ? reviewer : null,
      reviewedAt: reviewed ? isoOf(reviewedMs) : null,
      reviewNote: reviewed ? (note ?? null) : null,
      mergedIntoId: mergedInto ? `d-${idPrefix}${pad(mergedInto)}` : null,
      deletedAt: null,
      deletedBy: null,
    };
    created.push(d);
    const label = formatDamageNumber(number, code);
    const initialStatus: DamageStatus = byMember
      ? 'gemeldet'
      : status === 'behoben' || status === 'in_reparatur'
        ? 'bekannt'
        : status;
    pushEvent({
      actorId: reporter,
      entityType: 'damage',
      entityId: id,
      entityLabel: label,
      damageId: id,
      action: 'created',
      before: null,
      after: { title, description, category, categories, status: initialStatus, view, x, y, nearby: [] },
      reason: null,
      undoOf: null,
      createdAt: d.reportedAt,
    });
    const real = Object.hasOwn(realPhotos, title) ? realPhotos[title] : undefined;
    const photos = real ? real.length : placeholders;
    for (let p = 0; p < photos; p++) {
      const name = real?.[p];
      const overview = !real && photos > 1 && p === 0;
      const ref = `placeholder:${category}:${number * 10 + p}:${overview ? 1 : 0}`;
      ctx.images.push({
        id: `i-${idPrefix}${number}-${p}`,
        damageId: id,
        url: name ? demoPhotoRef(name) : ref,
        thumbUrl: name ? demoPhotoRef(name, true) : ref,
        width: name ? DEMO_PHOTOS[name].width : 1200,
        height: name ? DEMO_PHOTOS[name].height : 900,
        isOverview: overview,
        uploadedBy: reporter,
        createdAt: d.reportedAt,
        deletedAt: null,
        deletedBy: null,
      });
    }
    if (photos > 0) {
      pushEvent({
        actorId: reporter,
        entityType: 'damage_image',
        entityId: `i-${idPrefix}${number}-0`,
        entityLabel: label,
        damageId: id,
        action: 'image_added',
        before: null,
        after: { count: photos },
        reason: null,
        undoOf: null,
        createdAt: d.reportedAt,
      });
    }
    if (status !== initialStatus) {
      const at = reviewed ? reviewedMs : (resolvedMs ?? reportedMs + 5 * DAY);
      pushEvent({
        actorId: reviewed ? reviewer : reporter,
        entityType: 'damage',
        entityId: id,
        entityLabel: label,
        damageId: id,
        action: 'status_changed',
        before: { status: initialStatus, reviewNote: null },
        after: {
          status,
          reviewNote: d.reviewNote,
          ...(d.mergedIntoId ? { mergedIntoNumber: mergedInto } : {}),
        },
        reason: d.reviewNote,
        undoOf: null,
        createdAt: isoOf(at),
      });
    }
  });
  ctx.damages.push(...created);
  return created;
}

function tenant(id: string, name: string, slug: string, code: string, createdMs: number, extra: Partial<StoredTenant>): StoredTenant {
  return {
    id,
    name,
    slug,
    code,
    clubName: null,
    street: null,
    postalCode: null,
    city: null,
    country: null,
    phone: null,
    contactEmail: null,
    website: null,
    notes: null,
    isActive: true,
    createdAt: isoOf(createdMs),
    ...extra,
  };
}

export function createSeed(nowDate: Date = new Date()): DemoState {
  const now = nowDate.getTime();
  const { musterstadt: mst, beispieltal: bsp } = DEMO_TENANTS;

  const tenants: StoredTenant[] = [
    tenant(mst, 'Flugsportverein Musterstadt e.V.', 'musterstadt', 'FSVM', now - 400 * DAY, {
      clubName: 'Flugsportverein Musterstadt e.V.',
      street: 'Am Flugplatz 1',
      postalCode: '12345',
      city: 'Musterstadt',
      country: 'Deutschland',
      website: 'https://musterstadt.example.org',
    }),
    tenant(bsp, 'Luftsportgruppe Beispieltal', 'beispieltal', 'LSGB', now - 20 * DAY, {
      clubName: 'Luftsportgruppe Beispieltal e.V.',
      postalCode: '54321',
      city: 'Beispieltal',
      country: 'Deutschland',
      contactEmail: 'technik.beispieltal@example.org',
      notes: 'Pilot seit Oktober 2026, Ansprechpartner Jonas Becker.',
    }),
  ];

  const people: StoredPerson[] = [
    person('m-albrecht', 'Ines Albrecht', 1, now),
    person('m-becker', 'Jonas Becker', 0, now),
    person('m-hartmann', 'Tobias Hartmann', 3, now),
    person('m-kowalski', 'Marta Kowalski', 12, now),
    person('m-demir', 'Lea Demir', 0, now),
    // A further sign-in address (0023), so the member list shows one.
    person('m-nguyen', 'Felix Nguyen', 2, now, {
      additionalEmails: [{ id: 'e-nguyen-privat', email: 'felix.privat@example.org', source: 'self', createdAt: new Date(now - 6 * DAY).toISOString() }],
    }),
    person('m-schaefer', 'Paul Schäfer', 11, now),
    person('m-osei', 'Clara Osei', 20, now),
    person('m-brandt', 'Hannes Brandt', 9, now),
    person('m-haddad', 'Miriam Haddad', 0, now),
    person('m-petersen', 'Uwe Petersen', 50, now),
    person('m-lorenz', 'Katrin Lorenz', 4, now),
    person('m-jansen', 'Erik Jansen', 230, now),
    person('m-morina', 'Anja Morina', 5, now),
    person('m-arslan', 'Mehmet Arslan', 3, now),
    person('m-wagner', 'Sophie Wagner', 14, now),
  ];

  const ms = (tenantId: string, memberId: string, role: TenantRole, isActive = true): StoredMembership => ({
    tenantId,
    memberId,
    role,
    isActive,
    createdAt: isoOf(now - (tenantId === bsp ? 20 : 400) * DAY),
  });
  const memberships: StoredMembership[] = [
    ms(mst, 'm-albrecht', 'admin'),
    ms(mst, 'm-becker', 'editor'),
    ms(mst, 'm-hartmann', 'editor'),
    ms(mst, 'm-kowalski', 'editor'),
    ...['m-demir', 'm-nguyen', 'm-schaefer', 'm-osei', 'm-brandt', 'm-haddad', 'm-petersen', 'm-lorenz'].map((id) => ms(mst, id, 'member')),
    ms(mst, 'm-jansen', 'member', false),
    ms(bsp, 'm-becker', 'admin'),
    ms(bsp, 'm-morina', 'editor'),
    ms(bsp, 'm-arslan', 'member'),
    ms(bsp, 'm-wagner', 'member'),
  ];

  const rules = () => ({ unten: 'oben', rechts: 'links' }) as const;
  // Every demo type is a powered aeroplane (0025); the MS-1 is drawn from the PA28.
  const catalogue = (id: string, code: string, name: string, credit: string): StoredType => ({
    id,
    tenantId: null,
    code,
    name,
    credit,
    aircraftClass: 'motor',
    mirrorRules: { ...rules() },
    drawings: {},
    createdAt: isoOf(now - 800 * DAY),
  });
  // The club's own type: three uploads of the free demo set (sizes as in its drawings.json).
  const ms1Uploads: StoredDrawingUpload[] = (
    [
      ['oben', 1600, 1042],
      ['vorne', 1600, 358],
      ['links', 1600, 545],
    ] as const
  ).map(([view, width, height], i) => ({
    id: `u-ms1-${view}`,
    tenantId: mst,
    typeId: 't-ms1',
    view,
    src: `demo:PA28:${view}`,
    width,
    height,
    alpha: true,
    createdAt: isoOf(now - (29 - i) * DAY),
  }));
  const types: StoredType[] = [
    // credits as in the catalogue (0027): the demo shows the same drawings
    catalogue('t-da40', 'DA40', 'Diamond DA40', 'Zeichnung nach dem FlightGear-Modell von Emmanuel Baranger, GPL-2.0'),
    catalogue('t-c172', 'C172', 'Cessna 172', 'Zeichnung nach dem FlightGear-Modell des c172p-team, GPL-2.0'),
    catalogue('t-pa28', 'PA28', 'Piper PA-28-181 Archer', 'Zeichnung nach dem FlightGear-Modell von zlsa (Jon Ross), GPL-3.0'),
    {
      id: 't-ms1',
      tenantId: mst,
      code: 'MS-1',
      name: 'Musterstadt Eigenbau MS-1',
      credit: 'Fiktives Demo-Muster; Zeichnungen der PA-28 aus dem Demo-Satz (FlightGear-Modell von zlsa, GPL-3.0)',
      aircraftClass: 'motor',
      mirrorRules: { ...rules() },
      drawings: Object.fromEntries(ms1Uploads.map((u) => [u.view, { uploadId: u.id, width: u.width, height: u.height }])),
      createdAt: isoOf(now - 30 * DAY),
    },
  ];
  const ac = (id: string, tenantId: string, registration: string, typeId: string, sortOrder: number, extra: Partial<StoredAircraft> = {}): StoredAircraft => ({
    id,
    tenantId,
    registration,
    typeId,
    isActive: true,
    sortOrder,
    deletedAt: null,
    deletedBy: null,
    ...extra,
  });
  const mstAircraft: StoredAircraft[] = [
    ac('a-exaa', mst, 'D-EXAA', 't-da40', 1),
    ac('a-exab', mst, 'D-EXAB', 't-da40', 2),
    ac('a-exac', mst, 'D-EXAC', 't-da40', 3),
    ac('a-exad', mst, 'D-EXAD', 't-c172', 4),
    ac('a-exae', mst, 'D-EXAE', 't-pa28', 5),
  ];
  // Fleet admin only: out of service (no damages, so still deletable) and one in the trash.
  const mstAdminOnly: StoredAircraft[] = [
    ac('a-exaf', mst, 'D-EXAF', 't-ms1', 60, { isActive: false }),
    ac('a-exaz', mst, 'D-EXAZ', 't-c172', 70, { isActive: false, deletedAt: isoOf(now - 12 * DAY), deletedBy: DEMO_USERS.admin }),
  ];
  const bspAircraft: StoredAircraft[] = [ac('a-exba', bsp, 'D-EXBA', 't-da40', 1), ac('a-exbb', bsp, 'D-EXBB', 't-c172', 2)];

  const ctx: SeedContext = { now, events: [], damages: [], images: [] };
  const mstDamages = seedDamages(ctx, mst, 'FSVM', ROWS, mstAircraft, MST_EDITORS, 'm-becker', '', MST_PHOTOS);
  const bspDamages = seedDamages(ctx, bsp, 'LSGB', BSP_ROWS, bspAircraft, BSP_EDITORS, 'm-morina', 'bt-');
  const pushEvent = (e: Omit<StoredEvent, 'id'>) => ctx.events.push(e);

  // A correction and a merged report, so the timeline has more than one kind of entry.
  const d1 = mstDamages[0];
  if (d1) {
    pushEvent({
      tenantId: mst,
      actorId: 'm-hartmann',
      entityType: 'damage',
      entityId: d1.id,
      entityLabel: formatDamageNumber(1, 'FSVM'),
      damageId: d1.id,
      action: 'updated',
      before: { title: 'Kratzer Flügel links', description: 'Ca. 6 cm lang, Lack durch.' },
      after: { title: d1.title, description: d1.description },
      reason: null,
      undoOf: null,
      createdAt: isoOf(Date.parse(d1.reportedAt) + 3 * DAY),
    });
  }
  const target = mstDamages.find((d) => d.number === 15);
  if (target) {
    pushEvent({
      tenantId: mst,
      actorId: 'm-becker',
      entityType: 'damage',
      entityId: target.id,
      entityLabel: formatDamageNumber(15, 'FSVM'),
      damageId: target.id,
      action: 'merged_report',
      before: null,
      after: { images: 1, from: 18 },
      reason: 'Duplikat von FSVM-000015',
      undoOf: null,
      createdAt: isoOf(now - 48 * DAY),
    });
  }
  pushEvent({
    tenantId: mst,
    actorId: 'm-albrecht',
    entityType: 'setting',
    entityId: 'duplicate_radius',
    entityLabel: 'duplicate_radius',
    damageId: null,
    action: 'updated',
    before: { duplicateRadius: 0.07 },
    after: { duplicateRadius: 0.085 },
    reason: null,
    undoOf: null,
    createdAt: isoOf(now - 35 * DAY),
  });
  pushEvent({
    tenantId: mst,
    actorId: 'm-albrecht',
    entityType: 'member',
    entityId: 'm-jansen',
    entityLabel: 'Erik Jansen',
    damageId: null,
    action: 'updated',
    before: { role: 'member', isActive: true },
    after: { role: 'member', isActive: false },
    reason: 'Austritt zum 30.06.',
    undoOf: null,
    createdAt: isoOf(now - 95 * DAY),
  });
  const fleetEvent = (entityType: 'aircraft' | 'aircraft_type', entityId: string, entityLabel: string, action: 'created' | 'updated' | 'deleted', before: StoredEvent['before'], after: StoredEvent['after'], daysAgo: number) =>
    pushEvent({ tenantId: mst, actorId: DEMO_USERS.admin, entityType, entityId, entityLabel, damageId: null, action, before, after, reason: null, undoOf: null, createdAt: isoOf(now - daysAgo * DAY) });
  fleetEvent('aircraft_type', 't-ms1', 'MS-1', 'created', null, { code: 'MS-1', name: 'Musterstadt Eigenbau MS-1', credit: types[3]?.credit ?? '', aircraftClass: 'motor' }, 30);
  for (const u of ms1Uploads) {
    fleetEvent('aircraft_type', 't-ms1', 'MS-1', 'updated', { view: u.view, blob: null, mirroredFrom: null }, { view: u.view, blob: u.id, width: u.width, height: u.height }, (now - Date.parse(u.createdAt)) / DAY);
  }
  fleetEvent('aircraft', 'a-exaf', 'D-EXAF', 'created', null, { registration: 'D-EXAF', typeId: 't-ms1', typeCode: 'MS-1', sortOrder: 60, isActive: true }, 26);
  fleetEvent('aircraft', 'a-exaf', 'D-EXAF', 'updated', { isActive: true }, { isActive: false }, 25);
  fleetEvent('aircraft', 'a-exaz', 'D-EXAZ', 'created', null, { registration: 'D-EXAZ', typeId: 't-c172', typeCode: 'C172', sortOrder: 70, isActive: false }, 14);
  fleetEvent('aircraft', 'a-exaz', 'D-EXAZ', 'deleted', { deletedAt: null }, { deletedAt: isoOf(now - 12 * DAY) }, 12);
  // Notices of the technicians (0022): one of each level in Musterstadt, where
  // grounding is switched on, and a resolved one for the aircraft's history.
  // D-EXAA (the tour's aircraft) only has the resolved one: the tour reports there.
  const notice = (id: string, aircraftId: string, level: StoredNotice['level'], title: string, body: string | null, daysAgo: number, resolvedDaysAgo: number | null = null): StoredNotice => ({
    id,
    tenantId: mst,
    aircraftId,
    level,
    title,
    body,
    validUntil: null,
    createdAt: isoOf(now - daysAgo * DAY),
    createdBy: DEMO_USERS.editor,
    revisedAt: isoOf(now - daysAgo * DAY),
    updatedAt: isoOf(now - (resolvedDaysAgo ?? daysAgo) * DAY),
    resolvedAt: resolvedDaysAgo === null ? null : isoOf(now - resolvedDaysAgo * DAY),
    resolvedBy: resolvedDaysAgo === null ? null : DEMO_USERS.editor,
  });
  const notices: StoredNotice[] = [
    notice('n-exab-heading', 'a-exab', 'einschraenkung', 'Kurskreisel INOP', 'Ersatzteil ist bestellt, Einbau voraussichtlich nächste Woche. Kurs nach Magnetkompass.', 3),
    notice('n-exac-autopilot', 'a-exac', 'info', 'Autopilot schlingert leicht im Reiseflug', 'Fehlersuche läuft. Auffälligkeiten bitte als Rückfrage/Hinweis am Flugzeug notieren.', 1),
    notice('n-exad-brake', 'a-exad', 'gesperrt', 'Bremse links undicht', 'Bremsflüssigkeit tritt aus. Bis zur Reparatur gesperrt.', 0.5),
    notice('n-exaa-radio', 'a-exaa', 'info', 'Funkgerät rauscht auf 122,705', null, 12, 9),
  ];
  const noticeAcks: StoredNoticeAck[] = [
    { noticeId: 'n-exab-heading', memberId: 'm-nguyen', acknowledgedAt: isoOf(now - 2 * DAY) },
    { noticeId: 'n-exab-heading', memberId: 'm-schaefer', acknowledgedAt: isoOf(now - 1 * DAY) },
  ];
  const registrationOf = (aircraftId: string) => mstAircraft.find((a) => a.id === aircraftId)?.registration ?? '';
  for (const n of notices) {
    const label = `${registrationOf(n.aircraftId)} · ${n.title}`;
    pushEvent({ tenantId: mst, actorId: n.createdBy, entityType: 'aircraft_notice', entityId: n.id, entityLabel: label, damageId: null, action: 'created', before: null, after: { registration: registrationOf(n.aircraftId), level: n.level, title: n.title, body: n.body, validUntil: null }, reason: null, undoOf: null, createdAt: n.createdAt });
    if (n.resolvedAt && n.resolvedBy) {
      pushEvent({ tenantId: mst, actorId: n.resolvedBy, entityType: 'aircraft_notice', entityId: n.id, entityLabel: label, damageId: null, action: 'status_changed', before: { resolved: false, level: n.level }, after: { resolved: true, level: n.level }, reason: null, undoOf: null, createdAt: n.resolvedAt });
    }
  }
  // An export of the club, 5 days ago (the archive is rebuilt when it is downloaded).
  const exportAt = isoOf(now - 5 * DAY);
  pushEvent({
    tenantId: mst,
    actorId: DEMO_USERS.admin,
    entityType: 'tenant',
    entityId: mst,
    entityLabel: 'Flugsportverein Musterstadt e.V.',
    damageId: null,
    action: 'exported',
    before: null,
    after: { file: `${mst}/${exportStamp(exportAt)}.zip`, size: 0, tables: {}, photos: 0 },
    reason: null,
    undoOf: null,
    createdAt: exportAt,
  });

  const comment = (list: StoredDamage[], n: number, authorId: string, body: string, hoursAfter: number): StoredComment | null => {
    const d = list[n - 1];
    if (!d) return null;
    return {
      id: `c-${d.id}-${authorId}-${hoursAfter}`,
      damageId: d.id,
      authorId,
      body,
      createdAt: isoOf(Math.min(now - 600_000, Date.parse(d.reportedAt) + hoursAfter * 3_600_000)),
      deletedAt: null,
      deletedBy: null,
    };
  };
  const comments = [
    comment(mstDamages, 3, 'm-becker', 'Danke! Kannst du beim nächsten Flug ein Übersichtsfoto aus 1–2 m Abstand nachreichen? Dann sehen wir, ob es FSVM-000001 ist.', 20),
    comment(mstDamages, 3, 'm-nguyen', 'Mache ich, bin am Samstag wieder draußen.', 26),
    comment(mstDamages, 5, 'm-kowalski', 'Verkleidung kommt laut Diamond in KW 42. Bis dahin ohne Verkleidung fliegen ist freigegeben.', 30),
    comment(mstDamages, 13, 'm-hartmann', 'Schaue ich mir am Wochenende an.', 50),
    comment(mstDamages, 11, 'm-demir', 'Ist nach dem letzten Flug unverändert.', 24 * 40),
    comment(bspDamages, 2, 'm-morina', 'Schaue ich mir vor dem nächsten Flug an. Bis dahin bitte nicht fliegen.', 4),
  ].filter((c): c is StoredComment => c !== null);
  for (const c of comments) {
    const d = ctx.damages.find((x) => x.id === c.damageId);
    const t = tenants.find((x) => x.id === d?.tenantId);
    if (!d || !t) continue;
    pushEvent({
      tenantId: d.tenantId,
      actorId: c.authorId,
      entityType: 'damage_comment',
      entityId: c.id,
      entityLabel: formatDamageNumber(d.number, t.code),
      damageId: c.damageId,
      action: 'created',
      before: null,
      after: { body: c.body },
      reason: null,
      undoOf: null,
      createdAt: c.createdAt,
    });
  }

  const events: StoredEvent[] = ctx.events
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .map((e, i) => ({ ...e, id: i + 1 }));

  return {
    version: DEMO_STATE_VERSION,
    damageCounters: { [mst]: ROWS.length, [bsp]: BSP_ROWS.length },
    nextEventId: events.length + 1,
    tenants,
    people,
    memberships,
    types: types.map((t) => ({ ...t, mirrorRules: { ...t.mirrorRules }, drawings: { ...t.drawings } })),
    drawingUploads: ms1Uploads,
    aircraft: [...mstAircraft, ...mstAdminOnly, ...bspAircraft],
    damages: ctx.damages,
    images: ctx.images,
    comments,
    events,
    // Musterstadt keeps the default (members report: every /demo link lands there);
    // Beispieltal shows the other way: only technicians enter damages, members ask.
    // Musterstadt also shows grounding (0022): D-EXAD is grounded by a notice.
    settings: { [mst]: { ...DEFAULT_SETTINGS, aircraftGrounding: true }, [bsp]: { ...DEFAULT_SETTINGS, memberParticipation: 'comment', reviewDueDays: 14 } },
    exports: [{ tenantId: mst, name: `${mst}/${exportStamp(exportAt)}.zip`, size: 0, createdAt: exportAt, createdBy: DEMO_USERS.admin }],
    notices,
    noticeAcks,
  };
}
